import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Loader2, Check, Download, Music, Volume2, Wand2, Upload, CheckCircle } from 'lucide-react';
import api from '../services/api';

const EXPORT_PHASES = {
  downloading: {
    title: "Downloading",
    icon: Download,
    description: "Fetching audio files from storage...",
    estimatedTime: "~5 seconds"
  },
  censoring: {
    title: "Applying Censors",
    icon: Volume2,
    description: "Applying silence and beep effects to marked regions...",
    estimatedTime: "~10 seconds"
  },
  mixing: {
    title: "Mixing Audio",
    icon: Music,
    description: "Combining clean vocal with instrumental...",
    estimatedTime: "~10 seconds"
  },
  tagging: {
    title: "Adding Metadata",
    icon: Wand2,
    description: "Adding title, artist, and artwork tags...",
    estimatedTime: "~5 seconds"
  },
  uploading: {
    title: "Uploading",
    icon: Upload,
    description: "Saving clean version to storage...",
    estimatedTime: "~10 seconds"
  },
  complete: {
    title: "Complete",
    icon: CheckCircle,
    description: "Your clean edit is ready!",
    estimatedTime: ""
  },
  error: {
    title: "Error",
    icon: Download,
    description: "Something went wrong",
    estimatedTime: ""
  }
};

const PHASES_ORDER = ['downloading', 'censoring', 'mixing', 'tagging', 'uploading', 'complete'];

function PhaseIndicator({ phase, currentPhase, isComplete }) {
  const config = EXPORT_PHASES[phase] || EXPORT_PHASES.downloading;
  const Icon = config.icon;
  
  const phaseIndex = PHASES_ORDER.indexOf(phase);
  const currentIndex = PHASES_ORDER.indexOf(currentPhase);
  const isPast = phaseIndex < currentIndex || isComplete;
  const isCurrent = phase === currentPhase && !isComplete;
  
  return (
    <div className={`flex items-center gap-3 py-2 transition-all duration-300 ${
      isPast ? 'text-green-500' : 
      isCurrent ? 'text-primary font-medium' : 
      'text-muted-foreground/50'
    }`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
        isPast ? 'bg-green-500/20' : 
        isCurrent ? 'bg-primary/20' : 
        'bg-muted/30'
      }`}>
        {isPast ? (
          <Check className="w-4 h-4" />
        ) : isCurrent ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Icon className="w-4 h-4" />
        )}
      </div>
      <div className="flex-1">
        <span className="text-sm font-medium">{config.title}</span>
        {isCurrent && (
          <p className="text-xs text-muted-foreground mt-0.5">{config.description}</p>
        )}
      </div>
      {!isPast && !isCurrent && config.estimatedTime && (
        <span className="text-xs text-muted-foreground">{config.estimatedTime}</span>
      )}
    </div>
  );
}

export default function ExportProgressModal({ isOpen, sessionId, onClose, onComplete }) {
  const [progress, setProgress] = useState({
    phase: 'downloading',
    message: 'Starting export...',
    percent: 0
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !sessionId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/export-status`);
        const data = response.data;
        
        if (data.export_progress) {
          setProgress(data.export_progress);
        }
        
        if (data.export_progress?.phase === 'error') {
          setErrorMessage(data.export_progress.message || 'An error occurred during export');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
        
        if (data.export_progress?.phase === 'complete') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error('Error polling export status:', error);
      }
    };

    // Delay first poll to give backend time to reset the export progress
    const initialDelay = setTimeout(() => {
      pollStatus();
      pollIntervalRef.current = setInterval(pollStatus, 1000);
    }, 500);

    return () => {
      clearTimeout(initialDelay);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (isOpen) {
      setProgress({
        phase: 'downloading',
        message: 'Starting export...',
        percent: 0
      });
      setErrorMessage(null);
    }
  }, [isOpen]);

  const currentPhase = progress.phase || 'downloading';
  const isComplete = currentPhase === 'complete';
  const isError = currentPhase === 'error';

  const handleClose = () => {
    if (isComplete) {
      onComplete?.();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="export-progress-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {!isComplete && !isError && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isComplete && <CheckCircle className="h-5 w-5 text-green-500" />}
            <span>
              {isComplete ? 'Export Complete!' : 
               isError ? 'Export Failed' : 
               'Exporting Clean Version'}
            </span>
          </DialogTitle>
          <DialogDescription>
            {isComplete ? 'Your clean edit has been saved and is ready to download.' :
             isError ? 'There was a problem exporting your track.' :
             'Please wait while we create your clean edit...'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{progress.message}</span>
              <span className="text-primary font-medium">{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} className="w-full h-2" />
          </div>
          
          <div className="border rounded-lg p-4 bg-muted/20">
            <div className="space-y-1">
              {PHASES_ORDER.map((phase) => (
                <PhaseIndicator 
                  key={phase} 
                  phase={phase} 
                  currentPhase={currentPhase}
                  isComplete={isComplete}
                />
              ))}
            </div>
          </div>
          
          {isError && errorMessage && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-500">{errorMessage}</p>
            </div>
          )}
          
          {isComplete && (
            <div className="flex justify-center pt-2">
              <Button onClick={handleClose} className="w-full">
                <Check className="h-4 w-4 mr-2" />
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
